import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {extractNumber} from '@cred/shared/utils'
import { VALIDATE_EMAIL } from '@cred/react-auth-form/graphql/queries'

/*#__PURE__*/ dayjs.extend(customParseFormat)

const removeTime = date => date.hour(0).minute(0).second(0).millisecond(0)

const required = ({errorMessage = 'Please complete this field'} = {}) => value => {
    if (value === null || value === undefined ||
        typeof value === 'string' && value.trim() === '' ||
        typeof value === 'number' && (isNaN(value) || !isFinite(value))) {
      return errorMessage
    }

    return true
  },
  regex = ({regex, errorMessage = 'Invalid format'} = {}) => value => value && !regex.test(value) ? errorMessage : true,
  email = ({errorMessage = 'Please enter a valid email'} = {}) => regex({regex: /^[A-Z0-9._+-]+@[A-Z0-9-]+(\.[A-Z]+)+$/i, errorMessage}),
  dateRange = ({
    format = 'MM/DD/YYYY',
    minDate,
    maxDate,
    formatErrorMessage = 'Please enter a valid date',
    belowMinErrorMessage = `Please enter a date after ${minDate.format(format)}`,
    aboveMaxErrorMessage = `Please enter a date before ${maxDate.format(format)}`
  } = {}) => value => {
    const userDate = dayjs(value, format, true)

    if (
      value && (
      !userDate.isValid() || userDate.format(format) !== value ||
      userDate.toDate() < minDate.toDate() ||
      userDate.toDate() > maxDate.toDate())
    ) {
      if(!userDate.isValid() || userDate.format(format) !== value) {
        return formatErrorMessage
      } else if (userDate.toDate() < minDate.toDate()) {
        return belowMinErrorMessage
      } else {
        return aboveMaxErrorMessage
      }
    }

    return true
  },
  dob = ({format, formatErrorMessage, belowMinErrorMessage, aboveMaxErrorMessage} = {}) => dateRange({format, minDate: removeTime(dayjs().subtract(120, 'years')), maxDate: removeTime(dayjs().subtract(13, 'years')), formatErrorMessage, belowMinErrorMessage, aboveMaxErrorMessage}),
  completionDate = ({format = 'MM/YYYY', formatErrorMessage, belowMinErrorMessage, aboveMaxErrorMessage} = {}) => dateRange({format, minDate: removeTime(dayjs().date(1)), maxDate: removeTime(dayjs().add(10, 'years')), formatErrorMessage, belowMinErrorMessage, aboveMaxErrorMessage}),
  password = ({errorMessage = 'Please follow all password requirements'} = {}) => value =>
    !value || (
    /\d+/g.test(value) &&
    /[A-Z]+/g.test(value) &&
    /[a-z]+/g.test(value) &&
    /\S{8,}/g.test(value) &&
    /^[\S]+/g.test(value) &&
    /.*[\S]+$/g.test(value))
      ? true
      : errorMessage,
  futureDate = ({format, formatErrorMessage, belowMinErrorMessage, aboveMaxErrorMessage} = {}) => dateRange({format, minDate: dayjs(), maxDate: dayjs().add(1000, 'years'), formatErrorMessage, belowMinErrorMessage, aboveMaxErrorMessage}),
  pastDate = ({format, formatErrorMessage, belowMinErrorMessage, aboveMaxErrorMessage} = {}) => dateRange({format, minDate: dayjs().subtract(1000, 'years'), maxDate: dayjs(), formatErrorMessage, belowMinErrorMessage, aboveMaxErrorMessage}),
  phone = ({
    forbiddenAreaCodes = ['800', '811', '822', '833', '844', '855', '866', '877', '888'],
    lengthErrorMessage = 'Please enter a valid phone number',
    formatErrorMessage = 'Your phone number cannot start with a 1',
    areaCodeErrorMessage,
    repeatedCharsErrorMessage = 'Please enter a valid phone number'
  } = {}) => value => {
    if (value === '') {
      return true
    }

    const number = value.replace(/\D/g, ''),
      areaCode = number.substr(0, 3)

    if (number.length < 10) {
      return lengthErrorMessage
    } else if (number.charAt(0) === '1') {
      return formatErrorMessage
    } else if (forbiddenAreaCodes.includes(areaCode)) {
      return areaCodeErrorMessage || `Please enter a phone number that does not contain this area code: ${areaCode}`
    } else if (/^(\d)\1+$/.test(number)) {
      return repeatedCharsErrorMessage
    }

    return true
  },
  greaterThan = ({min, errorMessage = `Please enter a value greater than ${min}`} = {}) => value => value !== '' && (!extractNumber(value) || extractNumber(value) <= min) ? errorMessage : true,
  greaterThanZero = ({errorMessage} = {}) => greaterThan({errorMessage, min: 0}),
  lessThan = ({max, errorMessage = `Please enter a value less than ${max}`} = {}) => value => value !== '' && (!extractNumber(value) || extractNumber(value) >= max) ? errorMessage : true,
  ssn = ({
    lengthErrorMessage = 'Please enter a valid SSN',
    repeatedErrorMessage = 'Please enter a valid SSN',
    formatErrorMessage = 'Please enter a valid SSN',
    allowedSecretSsn = '#########',
    bypassFormatFor = undefined
  } = {}) => value => {
    const valueWithoutMask = value && value.replace(/-/g, '')

    if (valueWithoutMask && !valueWithoutMask.match(/^\d{9}$/g) && valueWithoutMask !== allowedSecretSsn) {
      return lengthErrorMessage
    } else if (valueWithoutMask && valueWithoutMask.match(/^(\d)\1+$/g) && valueWithoutMask !== allowedSecretSsn) {
      return repeatedErrorMessage
    } else if(valueWithoutMask && (!bypassFormatFor || !bypassFormatFor.test(valueWithoutMask))) {
      if (valueWithoutMask && !/^(?!000)[0-8][0-9]{2}(?!00)[0-9]{2}(?!0000)[0-9]{4}$/g.test(valueWithoutMask) && valueWithoutMask !== allowedSecretSsn) {
        return formatErrorMessage
      }
    }

    return true
  },
  shortSsn = ({ errorMessage = 'Please enter a valid SSN' } = {}) => value => {
    if (value && !/^(?!0000)[0-9]{4}$/g.test(value) && value !== '####') {
      return errorMessage
    }

    return true
  },
  maxLength = ({length, errorMessage = `Please enter at most ${length} characters`} = {}) => value => value.length && value.length > length ? errorMessage : true,
  minLength = ({length, errorMessage = `Please enter at least ${length} characters`} = {}) => value => value.length && value.length < length ? errorMessage : true,
  validateEmailAsync = ({errorMessage = 'Please enter a valid email'} = {}, client, clearErrors, resetAuthFlows) => async(e) => {
    if(!!e && email()(e) === true) {
      if (clearErrors) {
        resetAuthFlows()
      }
      try {
        const {data: {validateEmail: {valid} = {}} = {}} = await client.query({query: VALIDATE_EMAIL, variables: {email: e}})
        return valid ? true : errorMessage
      } catch(e) {
        console.error(e.message || e)
      }
    }
  },
  zipCode = ({ errorMessage = 'Please enter a valid Zipcode' } = {}) => value => {
    if (value && !/(^\d{5}$)/.test(value)) {
      return errorMessage
    }

    return true
  }

  export {
    required,
    email,
    dateRange,
    dob,
    completionDate,
    password,
    futureDate,
    pastDate,
    phone,
    greaterThan,
    greaterThanZero,
    ssn,
    shortSsn,
    maxLength,
    regex,
    validateEmailAsync,
    zipCode,
    minLength,
    lessThan
  }